import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import { CrossCircleIcon } from "../../components/Icons";
import { convertUtcToLocalDate, formatUtcToLocalDateTime } from "../../utils/DateUtils";
import { getActivationStatusName } from "../../utils/LicenseUtils";
import { Licenseuser } from "../../types/Licenseuser";

type ModalProps = {
    showModal: boolean;
    selectedLicense: Licenseuser | undefined;
    handleClose: () => void;
    errorMessage: string;
};

/**
 * ライセンスユーザー詳細情報モーダル
 * ・[ライセンス詳細]にユーザー情報も付属した拡張版
 *
 * @param ModalProps
 * @returns JSX.Element
 */
const LicenseuserDetailModal: React.FC<ModalProps> = ({ showModal, selectedLicense, handleClose, errorMessage }) => {
    const { t, i18n } = useTranslation();

    return (
        <Modal
            className="fixed left-1/2 top-1/2 w-10/12 max-w-xl -translate-x-1/2 -translate-y-1/2 transform whitespace-pre-wrap break-words rounded-lg border bg-white p-2 shadow-md"
            overlayClassName="fixed inset-0 bg-black bg-opacity-50"
            isOpen={showModal}
        >
            <div className="m-0 items-center justify-center p-2 sm:m-2">
                <div className="pl-2 text-lg font-bold">{t("UserLicense.licenseDetailModalTitle")}</div>
                <hr className="my-2 border-t-2"></hr>

                {/* エラーメッセージ */}
                {errorMessage && (
                    <div className="flex items-center justify-center pt-2">
                        <CrossCircleIcon />
                        <span className="pl-1 text-center font-bold text-red-600">
                            {errorMessage.split("\n").map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </span>
                    </div>
                )}

                <div className="ml-5 flex w-full justify-center">
                    {selectedLicense && (
                        <div
                            className={`m-0 grid w-full grid-cols-3 items-center py-2 ${
                                i18n.language === "ja" ? "gap-x-8 text-base" : "gap-x-5 text-sm"
                            }`}
                        >
                            {/* 製品名 */}
                            <div className="col-span-1 pb-1">{t("CommonLabel.packageName")}</div>
                            <div className="col-span-2 pb-1">{selectedLicense.package_name}</div>

                            {/* ライセンスキー */}
                            <div className="col-span-1 pb-1">{t("CommonLabel.licenseKey")}</div>
                            <div className="col-span-2 pb-1">{selectedLicense.license_key}</div>

                            {selectedLicense.permanent_flg ? (
                                <>
                                    {/* 恒久版の場合 */}
                                    {/* 区分 */}
                                    <div className="col-span-1 pb-1">{t("CommonLabel.licenseClass")}</div>
                                    <div className="col-span-2 pb-1">{t("CommonLabel.licensePermanent")}</div>

                                    {/* 利用開始日 */}
                                    <div className="col-span-1 pb-1">{t("CommonLabel.activationDate")}</div>
                                    <div className="col-span-2 pb-1">
                                        {formatUtcToLocalDateTime(selectedLicense.activation_date)}
                                    </div>

                                    {/* サポート開始日時 */}
                                    <div className="col-span-1 pb-1">{t("CommonLabel.supportStartDate")}</div>
                                    <div className="col-span-2 pb-1">
                                        {formatUtcToLocalDateTime(selectedLicense.support_start_date)}
                                    </div>

                                    {/* サポート有効期限日時 */}
                                    <div className="col-span-1 pb-1">{t("CommonLabel.supportExpirationDate")}</div>
                                    <div className="col-span-2 pb-1">
                                        {formatUtcToLocalDateTime(selectedLicense.support_expiration_date)}
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* サブスクリプションの場合 */}
                                    {/* 区分 */}
                                    <div className="col-span-1 pb-1">{t("CommonLabel.licenseClass")}</div>
                                    {selectedLicense.free_trial_flg ? (
                                        <div className="col-span-2 pb-1">{t("CommonLabel.freeTrialVersion")}</div>
                                    ) : (
                                        <div className="col-span-2 pb-1">{t("CommonLabel.licenseSubscribe")}</div>
                                    )}

                                    {/* 利用開始日 */}
                                    <div className="col-span-1 pb-1">{t("CommonLabel.activationDate")}</div>
                                    <div className="col-span-2 pb-1">
                                        {formatUtcToLocalDateTime(selectedLicense.activation_date)}
                                    </div>

                                    {/* 有効期限 */}
                                    <div className="col-span-1 pb-1">{t("CommonLabel.expirationDate")}</div>
                                    <div className="col-span-2 pb-1">
                                        {formatUtcToLocalDateTime(selectedLicense.expire_date)}
                                    </div>
                                </>
                            )}

                            {/* 発行日 */}
                            <div className="col-span-1 pb-1">{t("CommonLabel.issueDate")}</div>
                            <div className="col-span-2 pb-1">
                                {formatUtcToLocalDateTime(selectedLicense.insert_date)}
                            </div>

                            {/* 使用状況 */}
                            <div className="col-span-1 pb-1">{t("CommonLabel.status")}</div>
                            <div className="col-span-2 pb-1">{getActivationStatusName(selectedLicense)}</div>

                            {/* コンピューター名 */}
                            <div className="col-span-1 pb-1">{t("CommonLabel.computerName")}</div>
                            <div className="col-span-2 pb-1">{selectedLicense.computer_name || "-"}</div>

                            <hr className="col-span-3 my-1 border-t-2 border-dotted"></hr>

                            {/* メールアドレス */}
                            <div className="col-span-1 pb-1">{t("CommonLabel.email")}</div>
                            <div className="col-span-2 pb-1">{selectedLicense.email}</div>

                            {/* 氏名 */}
                            <div className="col-span-1 pb-1">{t("CommonLabel.fullName")}</div>
                            <div className="col-span-2 pb-1">
                                {selectedLicense.family_name + " " + selectedLicense.given_name}
                            </div>

                            {/* 所属 */}
                            <div className="col-span-1 pb-1">{t("CommonLabel.organization")}</div>
                            <div className="col-span-2 pb-1">{selectedLicense.organization}</div>
                        </div>
                    )}
                </div>
                {selectedLicense?.permanent_flg &&
                    new Date() > convertUtcToLocalDate(selectedLicense.support_expiration_date) && (
                        <div className="flex items-center justify-center pb-2 font-bold text-orange-700">
                            {t("UserLicense.licenseSupportExpired")}
                        </div>
                    )}

                {/* キャンセルボタン */}
                <div className="col-span-2 mt-3 flex items-center justify-evenly">
                    <button className="btn-secondary w-1/3" onClick={handleClose}>
                        {t("CommonLabel.close")}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default LicenseuserDetailModal;
